/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import { CSSProperties, forwardRef } from "react";
import ReactSelect, { GetOptionLabel, GetOptionValue } from "react-select";
import SelectType from "react-select/dist/declarations/src/Select";
import Feedback from "../feedback";
import { IFeedback } from "../types";

export interface IOption {
    value: string;
    label: string;
    data?: any;
    readonly color?: string;
    readonly isFixed?: boolean;
    readonly isDisabled?: boolean;
}

export interface GroupedOption {
    readonly label: string;
    readonly options: readonly IOption[];
}

export interface ISelectProps extends IFeedback {
    options: IOption[] | GroupedOption[];
    defaultValue?: IOption;
    value?: IOption | null;
    name?: string;
    onChange?: (newValue: any) => void;
    readonly isClearable?: boolean;
    readonly isDisabled?: boolean;
    readonly isLoading?: boolean;
    readonly isRtl?: boolean;
    readonly isSearchable?: boolean;
    readonly isMulti?: boolean;
    placeholder?: string;
    feedbackText?: string;
    showBorder?: boolean;
    bgColor?: string;
    color?: string;
    fontSize?: number;
    getOptionLabel?: GetOptionLabel<IOption>;
    getOptionValue?: GetOptionValue<IOption>;
    height?: number;
}

const groupStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
};
const groupBadgeStyles: CSSProperties = {
    backgroundColor: "#EBECF0",
    borderRadius: "2em",
    color: "#172B4D",
    display: "inline-block",
    fontSize: 12,
    fontWeight: "normal",
    lineHeight: "1",
    minWidth: 1,
    padding: "0.16666666666667em 0.5em",
    textAlign: "center",
};

const formatGroupLabel = (data: GroupedOption) => (
    <div style={groupStyles}>
        <span>{data.label}</span>
        <span style={groupBadgeStyles}>{data.options.length}</span>
    </div>
);

const DropdownIndicator = () => {
    return (
        <span className="w-8 h-full inline-flex justify-center items-center">
            <i className="fa fa-caret-down" />
        </span>
    );
};

const Select = forwardRef<SelectType<IOption, boolean, GroupedOption>, ISelectProps>((
    {
        options,
        defaultValue,
        value,
        isClearable,
        isDisabled,
        isLoading,
        isRtl,
        isSearchable,
        isMulti,
        placeholder,
        onChange,
        showState,
        showErrorOnly,
        state,
        feedbackText,
        showBorder = true,
        bgColor = "white",
        color,
        fontSize = 12,
        getOptionLabel,
        getOptionValue,
        name,
        height = 32
    }, ref
) => {
    const customStyles = {
        control: (provided: any, inputState: any) => ({
            ...provided,
            fontSize,
            backgroundColor: bgColor,
            borderRadius: 6,
            borderColor: "#e5e7eb",
            border: showBorder ? "" : "none",
            minHeight: height,
            boxShadow: "none",
            color: color ?? "",
            marginTop: 0,
            "&:hover": {
                borderColor: inputState.isFocused && "#e6e7eb",
            },
        }),
        menu: (provided: any) => ({
            ...provided,
            borderRadius: 6,
            padding: 4,
            marginTop: -2,
            boxShadow: "none",
            border: "1px solid #e6e6e6",
            zIndex: 20,
            minWidth: "100px"
        }),
        option: (provided: any, inputState: any) => {
            const getColor = () => {
                if (inputState.isFocused && inputState.isSelected) return "#374151";
                if (inputState.isSelected) return "#868ba1";
                if (inputState.isFocused) return "#374151";
                return "#868ba1";
            };

            const getBackgroundColor = () => {
                if (inputState.isFocused && inputState.isSelected) return "#e6e7eb";
                if (inputState.isSelected) return "transparent";
                if (inputState.isFocused) return "#e6e7eb";
                if (inputState.isDisabled) return "#d8d9d9";
                return "transparent";
            };

            return {
                ...provided,
                color: getColor(),
                borderRadius: 6,
                fontSize: 12,
                backgroundColor: getBackgroundColor(),
                cursor: inputState.isDisabled ? "not-allowed" : "pointer",
                "&:hover": {
                    backgroundColor: !inputState.isDisabled ? "#e6e7eb" : provided.backgroundColor,
                    color: !inputState.isDisabled ? "#374151" : provided.color,
                },
            };
        },
        multiValue: (provided: any, inputState: any) => ({
            ...provided,
            backgroundColor: inputState.isDisabled ? "#adb5bd" : "#e6e7eb",
            borderRadius: 6,
            flexDirection: "row-reverse",
        }),
        multiValueLabel: (provided: any, inputState: any) => ({
            ...provided,
            color: "#374151",
            paddingRight: 10,
            paddingLeft: inputState?.isDisabled ? 10 : 0,
            paddingTop: 5,
        }),
        multiValueRemove: (provided: any, inputState: any) => ({
            ...provided,
            color: "#374151",
            opacity: 0.5,
            padding: 2,
            display: inputState.isDisabled ? "none" : "inline",
            "&:hover": {
                backgroundColor: "transparent",
                color: "#374151",
                opacity: 1,
            },
        }),
        singleValue: (provided: any, inputState: any) => ({
            ...provided,
            color: color ?? "374151",
        }),
        indicatorSeparator: () => ({
            display: "none",
        }),
        indicatorsContainer: () => ({
            marginLeft: "-13px",
        }),

    };
    return (
        <>
            <ReactSelect
                styles={customStyles}
                options={options}
                defaultValue={defaultValue}
                isClearable={isClearable}
                isDisabled={isDisabled}
                isLoading={isLoading}
                isRtl={isRtl}
                isSearchable={isSearchable}
                isMulti={isMulti}
                formatGroupLabel={formatGroupLabel}
                placeholder={placeholder}
                components={{ DropdownIndicator }}
                value={value}
                onChange={onChange}
                ref={ref}
                getOptionLabel={getOptionLabel}
                getOptionValue={getOptionValue}
                name={name}
            />
            {feedbackText && showState && (
                <Feedback state={state} showErrorOnly={showErrorOnly}>
                    {feedbackText}
                </Feedback>
            )}
        </>
    );
});

Select.displayName = "Select";

export default Select;
